import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import {
  AutomationJsonSchema,
  AutomationSchemaRef,
  SchemaValidationMode,
} from '@/types/automationFields.ts';
import { useMemo } from 'react';

type Props = {
  label: string;
  schemas: AutomationJsonSchema[];
  value?: AutomationSchemaRef | null;
  error?: string;
  description?: string;
  requiredValidationMode?: SchemaValidationMode;
  required?: boolean;
  onChange: (schemaRef: AutomationSchemaRef | null) => void;
};

const schemaRefToValue = (schemaRef?: AutomationSchemaRef | null) =>
  schemaRef ? JSON.stringify({ schema_id: schemaRef.schema_id, version: schemaRef.version }) : null;

const valueToSchemaRef = (value: string | null): AutomationSchemaRef | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as AutomationSchemaRef;
  } catch {
    return null;
  }
};

const schemaRefMatches = (schema: AutomationJsonSchema, schemaRef?: AutomationSchemaRef | null) =>
  schema.schema_id === schemaRef?.schema_id && schema.version === schemaRef?.version;

const makeSchemaLabel = (schemaRef: AutomationSchemaRef) =>
  `${schemaRef.schema_id} v${schemaRef.version}`;

const JsonSchemaRefSelect = ({
  label,
  schemas,
  value,
  error,
  description,
  requiredValidationMode,
  required = false,
  onChange,
}: Props) => {
  const selectedSchema = value ? schemas.find((schema) => schemaRefMatches(schema, value)) : null;
  const selectableSchemas = useMemo(
    () =>
      requiredValidationMode
        ? schemas.filter((schema) => schema.validation_mode === requiredValidationMode)
        : schemas,
    [requiredValidationMode, schemas],
  );
  const selectedSchemaHasRequiredMode =
    selectedSchema && (!requiredValidationMode || selectedSchema.validation_mode === requiredValidationMode);
  const options = useMemo(() => {
    const schemaOptions = selectableSchemas.map((schema) => ({
      value: schemaRefToValue(schema)!,
      label: makeSchemaLabel(schema),
    }));
    if (value && !selectedSchemaHasRequiredMode) {
      return [
        ...schemaOptions,
        {
          value: schemaRefToValue(value)!,
          label: selectedSchema
            ? `Ineligible Schema (${makeSchemaLabel(value)})`
            : `Deleted Schema (${makeSchemaLabel(value)})`,
        },
      ];
    }
    return schemaOptions;
  }, [selectableSchemas, selectedSchema, selectedSchemaHasRequiredMode, value]);

  const invalidSelectionError =
    value && !selectedSchema
      ? 'Selected schema is no longer defined'
      : value && !selectedSchemaHasRequiredMode
        ? `Selected schema must use ${requiredValidationMode} validation mode`
        : undefined;

  return (
    <StyledSelect
      label={label}
      description={description}
      placeholder={selectableSchemas.length > 0 ? 'Select a JSON schema' : 'No eligible schemas defined'}
      data={options}
      value={schemaRefToValue(value)}
      onChange={(nextValue) => onChange(valueToSchemaRef(nextValue))}
      required={required}
      clearable={!required}
      allowDeselect={!required}
      disabled={selectableSchemas.length === 0 && !value}
      error={error || invalidSelectionError}
    />
  );
};

export default JsonSchemaRefSelect;
